import { Button, Input, Select, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import FormPopup from '../../../components/Popup/FormPopup';
import Icon from '../../../components/Icon/Icon';
import UploadImage from '../../../components/UploadImage/UploadImage';
import useCurrentProfile from './useEditProfile';
import CurrentProfileConfigs from './EditCurrentProfile.configs';
import { Education, TabStatus, WorkExperience } from './EditProfile.model';
import styles from './EditProfile.module.scss';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Spinner from '../../../components/Spinner/Spinner';
import moment from 'moment';

const EditProfile = ({ control, errors }: any) => {
	const [editEducationForm, setEditEducationForm] = useState<string | undefined>('');
	const [editExperienceForm, setEditExperienceForm] = useState<string | undefined>('');

	const {
		deleteEducation,
		deleteWorkExperience,
		editEducation,
		editWorkExperience,
		formatDate,
		getFormPopupActions,
		setCurrentUser,
		state,
		submitEducation,
		submitWorkExperience,
		toggleImagePopup,
		toggleEducationForm,
		toggleExperienceForm,
	} = useCurrentProfile();

	const {
		conferences,
		currentUser,
		education,
		showEducationForm,
		showExperienceForm,
		showImagePopup,
		tabStatus,
		workExp,
	} = state;

	const {
		educationFields,
		i18nCountries,
		profileFields,
		researchFields,
		workExperienceFields,
	} = CurrentProfileConfigs();

	const intl = useIntl();
	const { TextArea } = Input;
	const { Option } = Select;

	const renderInput = (formField: any) => {
		return (
			<div
				className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
				key={formField.id}
			>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`userLabels.${formField.id}`} />
				</label>
				<Controller
					name={formField.id}
					control={control}
					rules={formField.rules}
					render={({ field }) => (
						<Input
							{...field}
							className={errors[formField.id] && 'error'}
							placeholder={intl.formatMessage({
								id: `formPlaceholders.${formField.id}`,
							})}
						/>
					)}
				/>
			</div>
		);
	};
	const renderTextArea = (formField: any) => {
		return (
			<div
				className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
				key={formField.id}
			>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`userLabels.${formField.id}`} />
				</label>
				<Controller
					name={formField.id}
					control={control}
					render={({ field }) => (
						<TextArea
							{...field}
							className={errors.firstName && 'error'}
							placeholder={intl.formatMessage({
								id: `formPlaceholders.${formField.id}`,
							})}
							rows={6}
						/>
					)}
				/>
			</div>
		);
	};
	const renderSelect = (formField: any) => {
		return (
			<div
				className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
				key={formField.id}
			>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`userLabels.${formField.id}`} />
				</label>
				<Controller
					name={formField.id}
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							className={styles.dropdown}
							placeholder={intl.formatMessage({
								id: `formPlaceholders.${formField.id}`,
							})}
						>
							{formField.options &&
								formField.options.optionList.map(
									(option: any, index: number) => (
										<Option
											key={`option${formField.id}${index}`}
											value={option.value}
										>
											{formField.options?.type === 'codeBased'
												? option.label
												: intl.formatMessage({
													id: `${formField.id}.${option.label}`,
												})}
										</Option>
									)
								)}
						</Select>
					)}
				/>
			</div>
		);
	};

	const renderSelectMultiple = (formField: any) => {
		return (
			<div
				className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
				key={formField.id}
			>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`userLabels.${formField.id}`} />
				</label>
				<Controller
					name={formField.id}
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							className={styles.dropdown}
							mode="multiple"
							filterOption={(input, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder={intl.formatMessage({
								id: `formPlaceholders.${formField.id}`,
							})}
							tagRender={({ label }) => (
								<Tag
									style={{
										border: 'none',
										textTransform: 'uppercase',
									}}
								>
									{label}
								</Tag>
							)}
						>
							{formField.options &&
								formField.options.optionList.map(
									(option: any, index: number) => (
										<Option
											key={`option${formField.id}${index}`}
											value={option.value}
										>
											{formField.options?.type === 'codeBased'
												? option.label
												: intl.formatMessage({
													id: `${formField.id}.${option.label}`,
												})}
										</Option>
									)
								)}
						</Select>
					)}
				/>
			</div>
		);
	};

	const renderTags = (formField: any) => {
		return (
			<div
				className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
				key={formField.id}
			>
				<label className={styles.floatingLabel}>
					<FormattedMessage id={`userLabels.${formField.id}`} />
				</label>
				<Controller
					name={formField.id}
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							className={styles.dropdown}
							mode="tags"
							placeholder={intl.formatMessage({
								id: `formPlaceholders.${formField.id}`,
							})}
							tagRender={({ label }) => (
								<Tag
									style={{
										border: 'none',
										textTransform: 'uppercase',
									}}
								>
									{label}
								</Tag>
							)}
						>
							{formField.options &&
								formField.options.optionList.map(
									(option: any, index: number) => (
										<Option
											key={`option${formField.id}${index}`}
											value={option.value}
										>
											{formField.options?.type === 'codeBased'
												? option.label
												: intl.formatMessage({
													id: `${formField.id}.${option.label}`,
												})}
										</Option>
									)
								)}
						</Select>
					)}
				/>
			</div>
		);
	};

	const renderField = (field: any, type: string) => {
		switch (type) {
			case 'text':
				return renderInput(field);
			case 'text-area':
				return renderTextArea(field);
			case 'select':
				return renderSelect(field);
			case 'selectMultiple':
				return renderSelectMultiple(field);
			case 'tags':
				return renderTags(field);
		}
	};

	const renderUserInfo = () => {
		return (
			<div className={styles.category}>
				<div className={styles.categoryTitle}>
					<Icon
						name="user-tie"
						color="light"
						width="18"
						height="18"
						className={styles.icon}
					/>
					<h2>{intl.formatMessage({ id: `userLabels.personalInfo` })}</h2>
				</div>
				<div className={styles.fields}>
					{profileFields.map(
						(field: any) =>
							(!field.dependency ||
								(currentUser &&
									currentUser[field.dependency.id] ===
									field.dependency.value)) &&
							renderField(field, field.type)
					)}
				</div>
			</div>
		);
	};
	const renderConference = () => {
		return (
			<div className={styles.category}>
				<div className={styles.categoryTitle}>
					<Icon
						name="clipboard-list"
						color="light"
						width="18"
						height="18"
						className={styles.icon}
						type="solid"
					/>
					<h2>{intl.formatMessage({ id: `userLabels.conference` })}</h2>
				</div>
				<div className={styles.fields}>
					<div
						className={`${styles.fieldWrapper} ${styles.w100}`}
						key={'conference'}
					>
						<label className={styles.floatingLabel}>
							<FormattedMessage id="userLabels.conference" />
						</label>
						<Controller
							name="conference"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									className={styles.dropdown}
									placeholder={intl.formatMessage({
										id: `formPlaceholders.conference`,
									})}
								>
									{conferences?.map(item => <Option value={item.id}>
								{item.name} {moment(item.date).format('DD MMM YYYY')}
							</Option>
								)}
								</Select>
							)}
						/>
					</div>
				</div>
			</div>
		);
	};

	const renderWorkExperienceItems = (workExp: WorkExperience[] | undefined) => {
		return workExp?.map((exp: WorkExperience, index: number) => (
			<div className={styles.expCard} key={exp.workExperienceId}>
				<div className={styles.expWrapper}>
					<div className={styles.content}>
						<div className={styles.expPeriod}>
							{formatDate(exp.startDate)} -{' '}
							{exp.endDate ? (
								formatDate(exp.endDate)
							) : (
								<FormattedMessage id="basics.present" />
							)}
						</div>
						<div className={styles.expPosition}>
							<b>
								<FormattedMessage
									id={`currentPosition.${exp.currentPosition}`}
								/>{' '}
							</b>
							<FormattedMessage id="basics.at" />
							<b> {exp.institution}</b>, {i18nCountries[exp.location]}
							<br />
							<span className={styles.description}>{exp.description}</span>
						</div>
					</div>
					<div className={styles.actions}>
						<Button
							className={`${styles.delete} ${styles.redButtonLink}`}
							onClick={() => deleteWorkExperience(exp.workExperienceId)}
							size="large"
							type="link"
						>
							<Icon name="trash" color="red" />
							<FormattedMessage id="buttons.delete" />
						</Button>
						<Button
							className={styles.edit}
							onClick={() => {
								setEditExperienceForm(exp.workExperienceId);
							}}
							size="large"
							type="default"
						>
							<FormattedMessage id="buttons.edit" />
						</Button>
					</div>
				</div>
			</div>
		));
	};

	const renderEducationItems = (education: Education[] | undefined) => {
		return education?.map((item: Education, index: number) => (
			<div className={styles.expCard} key={item.educationId}>
				<div className={styles.expWrapper}>
					<div className={styles.content}>
						<div className={styles.expPeriod}>
							{formatDate(item.startDate)} -{' '}
							{item.endDate ? (
								formatDate(item.endDate)
							) : (
								<FormattedMessage id="basics.present" />
							)}
						</div>
						<div className={styles.expPosition}>
							<b>{item.obtainedDegree} </b>
							<FormattedMessage id="basics.at" />
							<b> {item.institution}</b>, {i18nCountries[item.location]}
							<br />
							<span className={styles.description}>{item.description}</span>
						</div>
					</div>
					<div className={styles.actions}>
						<Button
							className={`${styles.delete} ${styles.redButtonLink}`}
							onClick={() => deleteEducation(item.educationId)}
							size="large"
							type="link"
						>
							<Icon name="trash" color="red" />
							<FormattedMessage id="buttons.delete" />
						</Button>
						<Button
							className={styles.edit}
							onClick={() => {
								setEditEducationForm(item.educationId);
							}}
							size="large"
							type="default"
						>
							<FormattedMessage id="buttons.edit" />
						</Button>
					</div>
				</div>
			</div>
		));
	};

	const renderResearchExperience = () => {
		return (
			<div className={styles.category}>
				<div className={styles.categoryTitle}>
					<Icon
						name="flask-potion"
						color="light"
						width="18"
						height="18"
						className={styles.icon}
					/>
					<h2>{intl.formatMessage({ id: `userLabels.workExperience` })}</h2>
				</div>
				<div className={styles.fields}>
					<div className={`${styles.fieldWrapper} ${styles.w100}`}>
						<label className={styles.floatingLabel}>
							<FormattedMessage id={`userLabels.researchAreas`} />
						</label>
						<Controller
							name="researchAreas"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									className={styles.dropdown}
									mode="multiple"
									tagRender={({ label }) => (
										<Tag
											style={{
												border: 'none',
												textTransform: 'uppercase',
											}}
										>
											{label}
										</Tag>
									)}
								>
									{researchFields.map((field) => (
										<Option key={field} value={field}>
											<FormattedMessage id={`researchAreas.${field}`} />
										</Option>
									))}
								</Select>
							)}
						/>
					</div>
					<div className={styles.workExp}>
						{renderWorkExperienceItems(workExp)}
						<button className={styles.addBtn} onClick={toggleExperienceForm}>
							<FormattedMessage id="buttons.addExperience" />
						</button>
					</div>
				</div>
			</div>
		);
	};

	const renderEducation = () => {
		return (
			<div className={styles.category}>
				<div className={styles.categoryTitle}>
					<Icon
						name="graduation-cap"
						color="light"
						width="18"
						height="18"
						className={styles.icon}
					/>
					<h2>{intl.formatMessage({ id: `userLabels.education` })}</h2>
				</div>
				<div className={styles.fields}>
					<div className={styles.workExp}>
						{renderEducationItems(education)}
						<button className={styles.addBtn} onClick={toggleEducationForm}>
							<FormattedMessage id="buttons.addEducation" />
						</button>
					</div>
				</div>
			</div>
		);
	};

	const renderContact = () => {
		return (
			<div className={styles.category}>
				<div className={styles.categoryTitle}>
					<Icon
						name="phone-alt"
						color="light"
						type="solid"
						width="18"
						height="18"
						className={styles.icon}
					/>
					<h2>{intl.formatMessage({ id: `userLabels.contactOptions` })}</h2>
				</div>
				<div className={styles.fields}>
					<div className={styles.contactCheckbox}>
						<Controller
							name="isReachableByText"
							control={control}
							render={({ field }) => (
								<Checkbox
									className="contactCheckbox"
									{...field}
									checked={field.value}
								>
									<FormattedMessage id="userLabels.isReachableByText" />
								</Checkbox>
							)}
						/>
					</div>
					<div className={styles.contactCheckbox}>
						<Controller
							name="isReachableByCall"
							control={control}
							render={({ field }) => (
								<Checkbox
									className="contactCheckbox"
									{...field}
									checked={field.value}
								>
									<FormattedMessage id="userLabels.isReachableByCall" />
								</Checkbox>
							)}
						/>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className={styles.tabContent}>
			<Spinner spinning={tabStatus === TabStatus.Loading} />

			{renderUserInfo()}
			{currentUser?.role === 'ApplicantMentee' && renderConference()}
			{renderResearchExperience()}
			{renderEducation()}
			{renderContact()}

			{showExperienceForm && (
				<FormPopup
					actions={getFormPopupActions(toggleExperienceForm)}
					title={{ action: 'add', target: 'experience' }}
					onSubmitForm={submitWorkExperience}
					onCancel={toggleExperienceForm}
					fields={workExperienceFields}
				/>
			)}
			{showEducationForm && (
				<FormPopup
					actions={getFormPopupActions(toggleEducationForm)}
					title={{ action: 'add', target: 'education' }}
					onSubmitForm={submitEducation}
					onCancel={toggleEducationForm}
					fields={educationFields}
				/>
			)}
			{editExperienceForm && (
				<FormPopup
					actions={getFormPopupActions(() => {
						setEditExperienceForm(undefined);
					})}
					title={{ action: 'edit', target: 'experience' }}
					editId={editExperienceForm}
					editData={workExp?.find(
						(exp) => exp.workExperienceId === editExperienceForm
					)}
					onSubmitForm={editWorkExperience}
					onCancel={() => {
						setEditExperienceForm(undefined);
					}}
					fields={workExperienceFields}
				/>
			)}
			{editEducationForm && (
				<FormPopup
					actions={getFormPopupActions(() => {
						setEditEducationForm(undefined);
					})}
					title={{ action: 'edit', target: 'education' }}
					editId={editEducationForm}
					editData={education?.find(
						(ed) => ed.educationId === editEducationForm
					)}
					onSubmitForm={editEducation}
					onCancel={() => {
						setEditEducationForm(undefined);
					}}
					fields={educationFields}
				/>
			)}
			{showImagePopup && (
				<UploadImage
					onClose={toggleImagePopup}
					currentUser={currentUser}
					afterClosed={(data) => {
						setCurrentUser(data);
					}}
				/>
			)}
		</div>
	);
};

export default EditProfile;
